import './Review.scss'
import {
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  Modal,
  Box,
  Skeleton,
} from '@mui/material'
import { LoadingButton } from 'components/mui'
import useReviewLogic from './Review.logic'
import { StickerPrintContainer } from 'components/organisms/TraySticker/TraySticker'
import { useState } from 'react'
import useDTMS3Image from 'lib/utils/useDTMS3Image'
import TraySubmissionResults from './TraySubmission/TraySubmissionResults'

// Create a SubTrayImage component for handling presigned URLs
const SubTrayImage = ({
  imageUrl,
  index,
}: {
  imageUrl: string
  index: number
}) => {
  const { imageUrl: trayImageSrc, isLoading } = useDTMS3Image({
    url: imageUrl,
    defaultImage: '',
  })

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width={80}
        height={80}
        animation="wave"
        className="sub-tray-image-skeleton"
      />
    )
  }

  return (
    <img
      src={trayImageSrc}
      alt={`Sub Tray ${index + 1}`}
      className="sub-tray-image"
    />
  )
}

const LogoutModal = ({
  open,
  onClose,
  onAddMore,
  onLogout,
}: {
  open: boolean
  onClose: () => void
  onAddMore: () => void
  onLogout: () => void
}) => (
  <Modal
    open={open}
    onClose={onClose}
    aria-labelledby="logout-confirmation"
    className="custom-modal"
  >
    <Box className="parent-modal-box">
      <Box className="header-box">
        <Typography variant="h6">Add Another Tray?</Typography>
      </Box>
      <Box className="child-box" sx={{ my: 1 }}>
        <Typography variant="body1">
          Would you like to add another tray? If not, you will be logged out in
          15 seconds.
        </Typography>
      </Box>
      <Box className="button-box" sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={onLogout}>Logout</Button>
        <Button variant="contained" onClick={onAddMore} autoFocus>
          Add Another Tray
        </Button>
      </Box>
    </Box>
  </Modal>
)

const Review = () => {
  const {
    allTrays,
    handleBack,
    handleDone,
    handleAddAnother,
    isCreatingTrayItem,
    showLogoutModal,
    handleModalClose,
    handleLogout,
    handlePrintLabel,
    isAssignedToCase,
    isFixingRejectedTray,
    selectedTrayCategory,
    submissionSummary,
    showSubmissionResults,
    handleRetryFailedTrays,
    handleNavigateToManagement,
    handleCloseSubmissionResults,
    isRetrying,
    isSubmittingFinalize,
    isSubmittingAddAnother,
  } = useReviewLogic()

  const [expandedCases, setExpandedCases] = useState<Record<string, boolean>>(
    {}
  )

  const toggleCaseExpand = (trayId: string) => {
    setExpandedCases((prev) => ({
      ...prev,
      [trayId]: !prev[trayId],
    }))
  }

  return (
    <div className="review-step">
      <Paper className="review-content">
        <Typography variant="h5" gutterBottom>
          Review Trays ({allTrays.length})
        </Typography>

        {allTrays.map((tray, index) => (
          <div key={tray.id} className="tray-review-item">
            <Typography
              variant="h5"
              className="tray-title"
              sx={{
                fontWeight: 'bold',
                padding: '10px 0',
                marginBottom: '15px',
                borderBottom: '2px solid var(--grayscale-border)',
              }}
            >
              Tray {index + 1}: {tray.trayDescription}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Barcode:
                </Typography>
                <Typography variant="body1">
                  {tray.trayBarcode || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Location:
                </Typography>
                <Typography variant="body1">
                  {tray.locationName || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Vendor:
                </Typography>
                <Typography variant="body1">
                  {tray.trayVendor || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Weight:
                </Typography>
                <Typography variant="body1">
                  {tray.trayWeight ? `${tray.trayWeight} lbs` : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Category:
                </Typography>
                <Typography variant="body1" textTransform="capitalize">
                  {selectedTrayCategory || 'N/A'}
                </Typography>
              </Grid>

              {(tray.surgery?.visit?.attendingProvider ||
                tray.selectedSurgeon ||
                tray.surgery?.procedures?.[0]?.description) && (
                <Grid item xs={12}>
                  <Box className="surgeon-case-info">
                    <Grid container spacing={2}>
                      {(tray.surgery?.visit?.attendingProvider ||
                        tray.selectedSurgeon) && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Surgeon
                          </Typography>
                          <Typography variant="body1">
                            {tray.surgery?.visit?.attendingProvider
                              ? `${
                                  tray.surgery.visit.attendingProvider.lastName
                                }, ${tray.surgery.visit.attendingProvider.firstName?.charAt(
                                  0
                                )}`
                              : tray.selectedSurgeon || 'N/A'}
                          </Typography>
                        </Grid>
                      )}

                      {tray.surgery?.procedures?.[0]?.description && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Operation
                          </Typography>
                          <div className="case-name-container">
                            <Typography
                              variant="body1"
                              className={
                                expandedCases[tray.id]
                                  ? 'expanded'
                                  : 'truncated'
                              }
                            >
                              {tray.surgery?.procedures?.[0]?.description ||
                                'N/A'}
                            </Typography>
                            {tray.surgery?.procedures?.[0]?.description &&
                              tray.surgery.procedures[0].description.length >
                                40 && (
                                <Button
                                  size="small"
                                  onClick={() => toggleCaseExpand(tray.id)}
                                  className="view-more-btn"
                                >
                                  {expandedCases[tray.id]
                                    ? 'View Less'
                                    : 'View More'}
                                </Button>
                              )}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {tray.subTrays.length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginTop: '10px',
                      marginBottom: '10px',
                      padding: '6px 0',
                      borderBottom: '1px solid var(--grayscale-border)',
                    }}
                  >
                    {tray.subTrays.length === 1
                      ? 'Tray'
                      : `Sub Trays (${tray.subTrays.length})`}
                  </Typography>
                  {tray.subTrays.map((subTray, subIndex) => (
                    <div key={subIndex} className="sub-tray-item">
                      <SubTrayImage
                        imageUrl={subTray.trayImg}
                        index={subIndex}
                      />
                      <div>
                        <Typography
                          variant="body2"
                          textTransform="capitalize"
                          color="text.secondary"
                        >
                          Type:{' '}
                          <span className="sub-tray-item_description">
                            {subTray.trayType}
                          </span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Description:{' '}
                          <span className="sub-tray-item_description">
                            {subTray.description}
                          </span>
                        </Typography>
                      </div>
                    </div>
                  ))}
                </Grid>
              )}
            </Grid>
          </div>
        ))}
      </Paper>

      <div className="buttons_container">
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        <Button variant="outlined" onClick={handlePrintLabel}>
          Print Labels
        </Button>
        {isFixingRejectedTray ? (
          <LoadingButton
            loading={isSubmittingFinalize}
            onClick={handleDone}
            variant="contained"
            disabled={isSubmittingFinalize}
          >
            Re-Submit Rejected Tray For Review
          </LoadingButton>
        ) : (
          <>
            <LoadingButton
              loading={isSubmittingAddAnother}
              onClick={handleAddAnother}
              variant="contained"
              disabled={isSubmittingAddAnother}
            >
              Finalize/ Submit & Load Tray(s) For <b>New Case</b>
            </LoadingButton>
            <LoadingButton
              loading={isSubmittingFinalize}
              onClick={handleDone}
              variant="contained"
              disabled={isSubmittingFinalize}
            >
              Finalize and Upload Trays
              <b>{isAssignedToCase ? 'For This Case' : ''}</b>
            </LoadingButton>
          </>
        )}
      </div>

      <LogoutModal
        open={showLogoutModal}
        onClose={handleModalClose}
        onAddMore={handleAddAnother}
        onLogout={handleLogout}
      />

      <TraySubmissionResults
        open={showSubmissionResults}
        onClose={handleCloseSubmissionResults}
        onRetryFailed={handleRetryFailedTrays}
        onNavigateToManagement={handleNavigateToManagement}
        summary={submissionSummary}
        isRetrying={isRetrying}
      />

      <StickerPrintContainer />
    </div>
  )
}

export default Review
